import { authFetch } from "@/lib/authFetch";
import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  User,
  Camera,
  Save,
  Menu,
  Upload,
  Building2,
  Phone,
  Globe,
  MapPin,
  FileText,
  Image,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import Sidebar from "@/components/Sidebar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Drag & Drop Upload Zone Component
const DropZone = ({ onFileSelect, accept, currentImage, label, icon: Icon, isUploading }) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith("image/")) {
      onFileSelect(file);
    } else {
      toast.error("Please drop an image file");
    }
  }, [onFileSelect]);

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      onFileSelect(file);
    }
  };

  return (
    <div
      className={`relative border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all
        ${isDragOver ? "border-[#84c77f] bg-[#84c77f]/10" : "border-gray-300 hover:border-[#357c2a]"}
        ${isUploading ? "opacity-50 pointer-events-none" : ""}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handleClick}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileChange}
        className="hidden"
      />
      
      {currentImage ? (
        <div className="relative inline-block">
          <img
            src={currentImage}
            alt={label}
            className="w-32 h-32 object-cover rounded-lg mx-auto"
          />
          <div className="absolute inset-0 bg-black/50 opacity-0 hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
            <Camera className="h-8 w-8 text-white" />
          </div>
        </div>
      ) : (
        <div className="py-4">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
            {Icon ? <Icon className="h-8 w-8 text-gray-400" /> : <Upload className="h-8 w-8 text-gray-400" />}
          </div>
          <p className="text-sm font-medium text-gray-700">{label}</p>
          <p className="text-xs text-gray-500 mt-1">Drag & drop or click to upload</p>
          <p className="text-xs text-gray-400 mt-1">PNG, JPG, GIF up to 5MB</p>
        </div>
      )}
      
      {isUploading && (
        <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded-xl">
          <div className="w-8 h-8 border-2 border-[#357c2a] border-t-transparent rounded-full animate-spin" />
        </div>
      )}
    </div>
  );
};

const ReferralPartnerProfile = () => {
  const { user, setUser } = useAuth();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingPicture, setIsUploadingPicture] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  
  const [profile, setProfile] = useState({
    profile_picture: null,
    company_logo: null,
    company_name: "",
    phone: "",
    website: "",
    address: "",
    bio: "",
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await authFetch(`${API}/users/referral-profile`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setProfile({
          profile_picture: data.profile?.profile_picture || null,
          company_logo: data.profile?.company_logo || null,
          company_name: data.profile?.company_name || "",
          phone: data.profile?.phone || "",
          website: data.profile?.website || "",
          address: data.profile?.address || "",
          bio: data.profile?.bio || "",
        });
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleProfilePictureUpload = async (file) => {
    setIsUploadingPicture(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await authFetch(`${API}/users/referral-profile/picture`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        setProfile((prev) => ({ ...prev, profile_picture: data.picture_url }));
        toast.success("Profile picture updated!");
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to upload picture");
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload picture");
    } finally {
      setIsUploadingPicture(false);
    }
  };

  const handleLogoUpload = async (file) => {
    setIsUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await authFetch(`${API}/users/referral-profile/logo`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        setProfile((prev) => ({ ...prev, company_logo: data.logo_url }));
        toast.success("Company logo updated!");
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to upload logo");
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload logo");
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const handleSaveProfile = async () => {
    setIsSaving(true);
    try {
      const response = await authFetch(`${API}/users/referral-profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          company_name: profile.company_name,
          phone: profile.phone,
          website: profile.website,
          address: profile.address,
          bio: profile.bio,
        }),
      });

      if (response.ok) {
        toast.success("Profile saved successfully!");
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to save profile");
      }
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Failed to save profile");
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setProfile((prev) => ({ ...prev, [field]: value }));
  };

  if (isLoading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? "bg-black" : "bg-slate-50"}`}>
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-[#357c2a] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className={isDarkMode ? "text-white" : "text-slate-600"}>Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen flex transition-colors duration-300 ${isDarkMode ? "bg-black" : "bg-slate-50"}`}>
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        isAdmin={false}
      />

      <div className="flex-1 flex flex-col min-h-screen overflow-hidden md:ml-64">
        <header className={`border-b px-4 md:px-6 py-4 transition-colors duration-300 ${isDarkMode ? "bg-black border-[#1a3d18]" : "bg-white border-slate-200"}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className={`md:hidden p-2 rounded-lg transition-colors ${isDarkMode ? "hover:bg-[#22c55e]/20 text-white" : "hover:bg-slate-100"}`}
              >
                <Menu className="h-5 w-5" />
              </button>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate("/team/dashboard")}
                  className={isDarkMode ? "text-slate-300 hover:text-white hover:bg-[#22c55e]/20" : ""}
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Dashboard
                </Button>
              </div>
            </div>
            <h1 className={`text-xl font-semibold transition-colors ${isDarkMode ? "text-white" : "text-slate-900"}`}>
              Referral Partner Profile
            </h1>
          </div>
        </header>

        <main className="flex-1 p-4 md:p-6 overflow-y-auto">
          <div className="max-w-4xl mx-auto space-y-6">
            {/* Profile & Logo Upload Section */}
            <Card className={`transition-colors duration-300 ${isDarkMode ? "bg-[#265324] border-[#1a3d18]" : ""}`}>
              <CardHeader>
                <CardTitle className={`flex items-center gap-2 ${isDarkMode ? "text-white" : ""}`}>
                  <Camera className="h-5 w-5 text-[#22c55e]" />
                  Profile Images
                </CardTitle>
                <CardDescription className={isDarkMode ? "text-slate-400" : ""}>
                  Upload your profile picture and company logo. These will be displayed to your referred clients.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label className={`block mb-2 ${isDarkMode ? "text-slate-300" : ""}`}>Profile Picture</Label>
                    <DropZone
                      onFileSelect={handleProfilePictureUpload}
                      accept="image/*"
                      currentImage={profile.profile_picture}
                      label="Your Photo"
                      icon={User}
                      isUploading={isUploadingPicture}
                    />
                  </div>
                  <div>
                    <Label className={`block mb-2 ${isDarkMode ? "text-slate-300" : ""}`}>Company Logo</Label>
                    <DropZone
                      onFileSelect={handleLogoUpload}
                      accept="image/*"
                      currentImage={profile.company_logo}
                      label="Company Logo"
                      icon={Building2}
                      isUploading={isUploadingLogo}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Contact Information Section */}
            <Card className={`transition-colors duration-300 ${isDarkMode ? "bg-[#265324] border-[#1a3d18]" : ""}`}>
              <CardHeader>
                <CardTitle className={`flex items-center gap-2 ${isDarkMode ? "text-white" : ""}`}>
                  <FileText className="h-5 w-5 text-[#22c55e]" />
                  Contact Information
                </CardTitle>
                <CardDescription className={isDarkMode ? "text-slate-400" : ""}>
                  This information will be visible to your referred clients in their portal.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="company_name" className={isDarkMode ? "text-slate-300" : ""}>
                      <Building2 className="h-4 w-4 inline mr-2" />
                      Company Name
                    </Label>
                    <Input
                      id="company_name"
                      value={profile.company_name}
                      onChange={(e) => handleChange("company_name", e.target.value)}
                      placeholder="Your Company Name"
                      className={`mt-1 ${isDarkMode ? "bg-[#1a3d18] border-[#22c55e] text-white" : ""}`}
                      data-testid="company-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className={isDarkMode ? "text-slate-300" : ""}>
                      <Phone className="h-4 w-4 inline mr-2" />
                      Phone Number
                    </Label>
                    <Input
                      id="phone"
                      value={profile.phone}
                      onChange={(e) => handleChange("phone", e.target.value)}
                      placeholder="(555) 123-4567"
                      className={`mt-1 ${isDarkMode ? "bg-[#1a3d18] border-[#22c55e] text-white" : ""}`}
                      data-testid="phone-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="website" className={isDarkMode ? "text-slate-300" : ""}>
                      <Globe className="h-4 w-4 inline mr-2" />
                      Website
                    </Label>
                    <Input
                      id="website"
                      value={profile.website}
                      onChange={(e) => handleChange("website", e.target.value)}
                      placeholder="https://yourcompany.com"
                      className={`mt-1 ${isDarkMode ? "bg-[#1a3d18] border-[#22c55e] text-white" : ""}`}
                      data-testid="website-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="address" className={isDarkMode ? "text-slate-300" : ""}>
                      <MapPin className="h-4 w-4 inline mr-2" />
                      Address
                    </Label>
                    <Input
                      id="address"
                      value={profile.address}
                      onChange={(e) => handleChange("address", e.target.value)}
                      placeholder="123 Main St, City, State"
                      className={`mt-1 ${isDarkMode ? "bg-[#1a3d18] border-[#22c55e] text-white" : ""}`}
                      data-testid="address-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="bio" className={isDarkMode ? "text-slate-300" : ""}>
                    About You / Bio
                  </Label>
                  <Textarea
                    id="bio"
                    value={profile.bio}
                    onChange={(e) => handleChange("bio", e.target.value)}
                    placeholder="Tell your clients a bit about yourself and your services..."
                    rows={4}
                    className={`mt-1 ${isDarkMode ? "bg-[#1a3d18] border-[#22c55e] text-white" : ""}`}
                    data-testid="bio-input"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Save Button */}
            <div className="flex justify-end">
              <Button
                onClick={handleSaveProfile}
                disabled={isSaving}
                className="bg-[#357c2a] hover:bg-[#265324] text-white px-8"
                data-testid="save-profile-btn"
              >
                {isSaving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Profile
                  </>
                )}
              </Button>
            </div>

            {/* Preview Card */}
            <Card className={`transition-colors duration-300 ${isDarkMode ? "bg-[#265324] border-[#1a3d18]" : ""}`}>
              <CardHeader>
                <CardTitle className={`flex items-center gap-2 ${isDarkMode ? "text-white" : ""}`}>
                  <User className="h-5 w-5 text-[#22c55e]" />
                  Preview - How Clients See You
                </CardTitle>
                <CardDescription className={isDarkMode ? "text-slate-400" : ""}>
                  This is how your profile will appear to clients in their portal.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className={`p-6 rounded-xl ${isDarkMode ? "bg-[#1a3d18]" : "bg-gray-50"}`}>
                  <div className="flex items-start gap-4">
                    {profile.profile_picture ? (
                      <img
                        src={profile.profile_picture}
                        alt="Profile"
                        className="w-20 h-20 rounded-full object-cover border-2 border-[#84c77f]"
                      />
                    ) : (
                      <div className="w-20 h-20 rounded-full bg-[#357c2a] flex items-center justify-center">
                        <User className="h-10 w-10 text-white" />
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}>
                          {user?.name || "Your Name"}
                        </h3>
                        {profile.company_logo && (
                          <img
                            src={profile.company_logo}
                            alt="Company"
                            className="h-8 w-auto"
                          />
                        )}
                      </div>
                      {profile.company_name && (
                        <p className={`text-sm ${isDarkMode ? "text-slate-400" : "text-gray-600"}`}>
                          {profile.company_name}
                        </p>
                      )}
                      <div className={`mt-3 space-y-1 text-sm ${isDarkMode ? "text-slate-300" : "text-gray-600"}`}>
                        {profile.phone && (
                          <p className="flex items-center gap-2">
                            <Phone className="h-4 w-4 text-[#84c77f]" />
                            {profile.phone}
                          </p>
                        )}
                        {profile.website && (
                          <p className="flex items-center gap-2">
                            <Globe className="h-4 w-4 text-[#84c77f]" />
                            {profile.website}
                          </p>
                        )}
                        {profile.address && (
                          <p className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-[#84c77f]" />
                            {profile.address}
                          </p>
                        )}
                      </div>
                      {profile.bio && (
                        <p className={`mt-3 text-sm ${isDarkMode ? "text-slate-400" : "text-gray-500"}`}>
                          {profile.bio}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ReferralPartnerProfile;
